import { Flex, SimpleGrid, Stack } from '@chakra-ui/react';
import React, { useState } from 'react';

import { ErrorInfoBar } from 'Containers/ProfilePage/_shared/ClaimsSummaryBar';
import { ClaimsSummary } from 'Containers/ProfilePage/types';
import { FormattedTooltipProps } from 'ContentfulDefaults/types/_shared';
import { useTextContext } from 'Contexts/textContext';
import { Button, H5, Icon, Numbers, Paragraph, Spinner, Tooltip } from 'DesignLibrary/atoms';
import { BannerCard } from 'DesignLibrary/molecules/BannerCard';
import InfoBar from 'DesignLibrary/molecules/InfoBar';
import Text from 'Shared/Text';
import { toDollars } from 'Utils/helpers';
import { sendInsightsEvent } from 'Utils/insights';

import {
  ClaimsCard,
  ClaimsTitle,
  ClaimsLoadingContainer,
  Content,
  SpinnerContainer,
  FeedbackText,
  IconButton,
  FeedBackInputWrapper,
  FeedbackInput,
  FeedbackContainer,
  CloseButton,
  FeedbackWrapper,
} from './styled';

export interface ClaimsBannerProps {
  handleOpen: () => void;
  claimsSummary: ClaimsSummary | null;
  claimsSummaryPolling: boolean;
  claimsError: Error | null;
  handleRetryClaims: () => void;
  handleCloseClaimsContent: () => void;
  claimsErrorState?: string | null;
}

export const ClaimsBanner = ({
  handleOpen,
  claimsSummary,
  claimsSummaryPolling,
  claimsError,
  handleRetryClaims,
  handleCloseClaimsContent,
  claimsErrorState,
}: ClaimsBannerProps) => {
  const { retrieveContentfulData } = useTextContext();
  const [feedback, setFeedback] = useState<string>('');
  const [feedbackInput, setFeedbackInput] = useState<string>('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState<boolean>(false);
  const [forceClose, setForceClose] = useState<boolean>(false);
  const currentMedicalSpendingData = retrieveContentfulData<{
    buttonText: string;
    comparisonHelpful: string;
    feedbackConfirmation: string;
    placeholderText: string;
  }>('claims_summary_bar.current_medical_spending');

  const sendAnalyticsEvent = () => {
    setFeedbackSubmitted(true);
    setFeedbackInput('');

    // send analytics event
    sendInsightsEvent(null, 'claims_feedback', {
      feedback,
      fibVerbatims: feedbackInput,
    });
  };

  const renderFeedBackComponent = (
    <FeedbackWrapper>
      <FeedbackText>
        {!feedbackSubmitted
          ? currentMedicalSpendingData.comparisonHelpful
          : currentMedicalSpendingData.feedbackConfirmation}
      </FeedbackText>

      {!feedbackSubmitted && (
        <>
          <IconButton aria-label="Not Helpful" active={feedback === 'no'} onClick={() => setFeedback('no')}>
            <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.66624 15.9339C9.00567 16.0866 10 14.9521 10 13.751V13.2504C10 11.9221 10.6467 11.1273 11.3175 10.6371C11.6458 10.3973 11.9778 10.2337 12.2361 10.1286C12.5391 10.6498 13.1037 11.0002 13.75 11.0002H14.75C15.7165 11.0002 16.5 10.2167 16.5 9.25024V1.7502C16.5 0.7837 15.7165 0.000200272 14.75 0.000200272H13.75C12.9753 0.000200272 12.3182 0.503599 12.0879 1.2012C11.5634 1.1263 11.0206 0.9725 10.3626 0.7862L10.3625 0.7861C10.2109 0.7432 10.0532 0.698501 9.88825 0.6527C8.73429 0.3322 7.34741 0.00039959 5.5 0.00039959C3.7943 0.00039959 2.5304 0.152699 1.7785 1.1396C1.4254 1.6029 1.2415 2.1816 1.1097 2.8117C0.9924 3.3726 0.9019 4.0554 0.7972 4.8452L0.7566 5.1512C0.507501 7.01959 0.4916 8.46916 0.9452 9.46699C1.1892 10.0036 1.5672 10.4096 2.0817 10.6671C2.576 10.9145 3.1469 11.0004 3.75 11.0004H5.1723L5.1573 11.1127V11.1127C5.0876 11.6309 5 12.2821 5 12.7504C5 13.6719 5.1513 14.469 5.6556 15.0498C6.1669 15.6388 6.9028 15.8469 7.66624 15.9339ZM12 2.7015C11.2947 2.613 10.6094 2.4174 9.9279 2.2228H9.92789C9.78074 2.1808 9.63376 2.1388 9.48678 2.098C8.39075 1.7935 7.15265 1.5004 5.5 1.5004C3.7058 1.5004 3.2197 1.723 2.9716 2.0486C2.8247 2.2415 2.6961 2.5535 2.5779 3.1187C2.4728 3.6211 2.3904 4.2428 2.2834 5.0487L2.2435 5.3495C1.9925 7.23113 2.054 8.28155 2.3107 8.84622C2.422 9.09086 2.5652 9.23174 2.7531 9.32577C2.9612 9.42992 3.2737 9.50036 3.75 9.50036H5.5004C6.1856 9.50036 6.79498 10.0773 6.70557 10.8363C6.68303 11.0276 6.65651 11.2253 6.63015 11.4217C6.5646 11.9102 6.50003 12.3914 6.50003 12.7504C6.50003 13.5589 6.64439 13.9007 6.78823 14.0664C6.92496 14.2239 7.18965 14.3699 7.83608 14.4436C8.14278 14.4785 8.50003 14.2065 8.50003 13.7509V13.2504C8.50003 11.3286 9.47829 10.1234 10.4325 9.42607C10.9026 9.08256 11.3684 8.85918 11.7152 8.72136C11.8212 8.67923 11.9171 8.64467 12 8.61682V2.7015ZM13.75 9.50024C13.6119 9.50024 13.5 9.38832 13.5 9.25024V2.0004V1.7502C13.5 1.6122 13.6119 1.5002 13.75 1.5002H14.75C14.8881 1.5002 15 1.6122 15 1.7502V9.25024C15 9.38832 14.8881 9.50024 14.75 9.50024H13.75Z"
                fill="#036991"
              />
            </svg>
          </IconButton>

          <IconButton aria-label="Helpful" active={feedback === 'yes'} onClick={() => setFeedback('yes')}>
            <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9.33376 0.0660893C7.99433 -0.086555 6.99998 1.04788 6.99997 2.24905V2.74964C6.99997 4.07792 6.35325 4.87266 5.68247 5.36285C5.35424 5.60271 5.02215 5.76632 4.76392 5.87138C4.46086 5.35017 3.89635 4.99976 3.25 4.99976H2.25C1.2835 4.99976 0.5 5.78326 0.5 6.74976V14.2498C0.5 15.2163 1.2835 15.9998 2.25 15.9998H3.25C4.0247 15.9998 4.68183 15.4964 4.91213 14.7988C5.43662 14.8737 5.97944 15.0275 6.63735 15.2138L6.63754 15.2139C6.78911 15.2568 6.94678 15.3015 7.11175 15.3473C8.26571 15.6678 9.65259 15.9996 11.5 15.9996C13.2057 15.9996 14.4696 15.8473 15.2215 14.8604C15.5746 14.3971 15.7585 13.8184 15.8903 13.1883C16.0076 12.6274 16.0981 11.9446 16.2028 11.1548L16.2434 10.8488C16.4925 8.98041 16.5084 7.53084 16.0548 6.53301C15.8108 5.99639 15.4328 5.5904 14.9183 5.33287C14.424 5.08545 13.8531 4.99964 13.25 4.99964H11.8277L11.8427 4.8873V4.88729C11.9124 4.36906 12 3.71789 12 3.24964C12 2.3281 11.8487 1.53102 11.3444 0.950213C10.8331 0.36121 10.0972 0.153092 9.33376 0.0660893ZM5 13.2985C5.70532 13.387 6.39055 13.5826 7.0721 13.7772H7.07211C7.21926 13.8192 7.36624 13.8612 7.51322 13.902C8.60925 14.2065 9.84735 14.4996 11.5 14.4996C13.2942 14.4996 13.7803 14.277 14.0284 13.9514C14.1753 13.7585 14.3039 13.4465 14.4221 12.8813C14.5272 12.3789 14.6096 11.7572 14.7166 10.9513L14.7565 10.6505C15.0075 8.76887 14.946 7.71845 14.6893 7.15378C14.578 6.90914 14.4348 6.76826 14.2469 6.67423C14.0388 6.57008 13.7263 6.49964 13.25 6.49964H11.4996C10.8144 6.49964 10.205 5.92267 10.2944 5.16372C10.317 4.97238 10.3435 4.77474 10.3699 4.57828C10.4354 4.08983 10.5 3.60865 10.5 3.24964C10.5 2.44105 10.3556 2.09928 10.2118 1.9336C10.075 1.77611 9.81035 1.63011 9.16392 1.55644C8.85722 1.52149 8.49997 1.79346 8.49997 2.24906V2.74964C8.49997 4.67136 7.52171 5.87662 6.5675 6.57393C6.09744 6.91744 5.6316 7.14082 5.28481 7.27864C5.17882 7.32077 5.08288 7.35533 5 7.38318V13.2985ZM3.25 6.49976C3.38807 6.49976 3.5 6.61168 3.5 6.74976V13.9996V14.2498C3.5 14.3878 3.38807 14.4998 3.25 14.4998H2.25C2.11193 14.4998 2 14.3878 2 14.2498V6.74976C2 6.61168 2.11193 6.49976 2.25 6.49976H3.25Z"
                fill="#036991"
              />
            </svg>
          </IconButton>
        </>
      )}
    </FeedbackWrapper>
  );

  const renderClaimsNumbers = () => {
    if (claimsSummaryPolling) {
      return (
        <ClaimsLoadingContainer data-testid="claims-loading-banner">
          <InfoBar
            text={
              <SpinnerContainer>
                <Spinner size="large" color="--primary-blue" />
                {retrieveContentfulData('health_section.secondary_content.claims_banner_loading')}
              </SpinnerContainer>
            }
          />
        </ClaimsLoadingContainer>
      );
    }

    if (claimsErrorState || claimsError || !claimsSummary) {
      return (
        <ErrorInfoBar
          error={claimsError}
          handleRetryClaims={handleRetryClaims}
          handleCloseClaimsContent={handleCloseClaimsContent}
          retryUrl="/profile/#claims-section"
          navigateToProfilePage
          claimsErrorState={claimsErrorState}
        />
      );
    }

    const { oopTowardDeductibleDollars, oopTowardMaxDollars, maxDollars, deductibleDollars } = claimsSummary;

    return (
      <SimpleGrid columns={[1, 2]} className="claims-numbers">
        <div>
          <Numbers size="medium">
            {toDollars(oopTowardDeductibleDollars, 2)}
            <span> /</span>
          </Numbers>{' '}
          <Numbers size="small">{toDollars(deductibleDollars)}</Numbers>
          {retrieveContentfulData<string>('tool_tips.deductible.title').toLowerCase()}
        </div>
        <div>
          <Numbers size="medium">
            {toDollars(oopTowardMaxDollars, 2)}
            <span> /</span>
          </Numbers>{' '}
          <Numbers size="small">{toDollars(maxDollars)}</Numbers>
          {retrieveContentfulData('health_section.secondary_content.claims_banner_oop_max')}
        </div>
      </SimpleGrid>
    );
  };

  return (
    <BannerCard>
      <ClaimsCard>
        <Stack>
          <ClaimsTitle>
            <Flex align="center">
              <Icon type="Calculator" size="large" color="--primary-blue" weight="fill" />
              <H5 weight="normal" data-testid="claims-banner-header">
                <Text field="health_section.secondary_content.claims_banner_header" />
              </H5>
              <Tooltip
                id="member-medical-spending-tooltip"
                label={retrieveContentfulData<FormattedTooltipProps>(
                  'claims_summary_bar.claims_data_tooltip',
                )}
              />
            </Flex>
          </ClaimsTitle>
        </Stack>
        <Content>
          {renderClaimsNumbers()}

          {!feedback && !feedbackSubmitted && !forceClose && renderFeedBackComponent}

          {(feedback || feedbackSubmitted) && !forceClose && (
            <FeedbackContainer>
              <CloseButton
                aria-label="Close Feedback"
                onClick={() => (feedbackSubmitted ? setForceClose(true) : setFeedback(''))}
              >
                <Icon type="X" color="#828297" size={14} />
              </CloseButton>
              {renderFeedBackComponent}
              {!feedbackSubmitted && (
                <FeedBackInputWrapper>
                  <FeedbackInput
                    placeholder={currentMedicalSpendingData.placeholderText}
                    maxLength={500}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                      if (feedbackInput.length >= 500) {
                        return;
                      }
                      setFeedbackInput(e.target.value);
                    }}
                  />
                  <Button
                    size="small"
                    buttonType="primary"
                    stretch
                    isDisabled={false}
                    onClick={sendAnalyticsEvent}
                    isLoading={false}
                  >
                    {currentMedicalSpendingData.buttonText}
                  </Button>
                </FeedBackInputWrapper>
              )}
            </FeedbackContainer>
          )}

          <hr />
          <Paragraph size="small">
            <Text field="health_section.secondary_content.claims_banner_subtext" />
          </Paragraph>
          <Button size="small" stretch onClick={handleOpen} data-testid="claims-breakdown-button">
            <Text field="health_section.secondary_content.claims_banner_button" />
          </Button>
        </Content>
      </ClaimsCard>
    </BannerCard>
  );
};
